import useAccountQuery from 'api/queries/useAccountQuery';
import EBTCardSummary from 'features/shop/cart/EBTPaymentFlow/shared/EBTCardSummary';
import {
    CreditCardCharge,
    StepCard,
    StepHeading,
    SuccessIcon,
} from 'features/shop/cart/EBTPaymentFlow/shared/FlowPresentation';
import Button from 'honeydew/Button';
import { EBTCardLast4 } from 'shared/ebt/EBTSharedUI';

/**
 * EditEBTPaymentSuccess component displays the success state after editing an EBT payment.
 *
 * This component handles two scenarios:
 * 1. Refund success: When the new payment amount is less than the current amount,
 *    showing refund information and processing timeline
 * 2. Payment success: When a new payment is successfully processed,
 *    showing payment confirmation and remaining balance
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.ebtCard - EBT card information
 * @param {string} props.ebtCard.last4 - Last 4 digits of the EBT card number
 * @param {Object} props.flowData - Flow state data containing payment and refund information
 * @param {boolean} props.flowData.isRefund - Whether this is a refund scenario (new amount < current amount)
 * @param {number} props.flowData.refundAmount - Amount to be refunded (current amount - new amount)
 * @param {Object} props.flowData.apiResponse - API response from payment processing
 * @param {number} props.flowData.apiResponse.ebtAmount - The EBT payment amount that was processed
 * @param {Function} props.handleClose - Callback function to close the payment flow modal
 *
 * @returns {JSX.Element} The rendered success confirmation UI
 */
function EditEBTPaymentSuccess({ ebtCard, flowData, handleClose }) {
    const { data: account = {} } = useAccountQuery();
    const { isRefund, refundAmount } = flowData;
    const apiResponse = flowData?.apiResponse?.data;

    return (
        <div className="space-y-15">
            {isRefund ? (
                <>
                    <div className="flex items-center gap-5">
                        <SuccessIcon />
                        <StepHeading>Your refund is on its way</StepHeading>
                    </div>
                    <p>
                        <span className="text-brand-primary font-grotesk-bold">${refundAmount?.toFixed(2)}</span> will
                        be refunded to <EBTCardLast4 last4={ebtCard?.last4} className="font-grotesk-bold" /> and should
                        be processed within one business day.
                    </p>
                    <CreditCardCharge
                        orderChargeDate={account?.nextOrder?.orderChargeDate?.date}
                        marketCloseDate={account?.marketCloseDate?.date}
                    />
                </>
            ) : (
                <>
                    <div className="flex items-center gap-5">
                        <SuccessIcon />
                        <StepHeading>Success!</StepHeading>
                    </div>
                    <p>
                        Payment of{' '}
                        <span className="text-brand-primary font-grotesk-bold">
                            ${apiResponse?.ebtAmount?.toFixed(2)}
                        </span>{' '}
                        from <EBTCardLast4 last4={ebtCard?.last4} className="font-grotesk-bold" /> has been applied to
                        your order.
                    </p>
                    <CreditCardCharge
                        orderChargeDate={account?.nextOrder?.orderChargeDate?.date}
                        marketCloseDate={account?.marketCloseDate?.date}
                    />
                    <StepCard>
                        <EBTCardSummary balanceLabel="Remaining Balance" last4={ebtCard?.last4} />
                    </StepCard>
                </>
            )}
            <Button onClick={handleClose}>Done</Button>
        </div>
    );
}

export default EditEBTPaymentSuccess;
